import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { EMPTY, of, timer } from 'rxjs';
import { catchError, delayWhen, filter, map, mergeMap, switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { Delivery, GroupTypeKey } from '../../../model/group/group';
import { ReleaseStatusKey } from '../../../model/release-status/release-status';
import { AccessApiService } from '../../../shared/access-api/access-api.service';
import { AlertService, AlertType } from '../../../shared/alert/alert.service';
import { FileParserService } from '../../../shared/file-parser/file-parser.service';
import { loadApiMetadataPipelineRunSummary } from '../api-metadata-validator/api-metadata-validator.actions';
import { loadPipelineRunATCInformation } from '../atc/atc.actions';
import { loadCentralPolicyResultsSingleSummary } from '../central-policy-results/central-policy-results.actions';
import { loadCodeCoverageForPipelineRun } from '../code-coverage-info/code-coverage-info.actions';
import { loadCustomPolicyResultsSingleSummary } from '../custom-policy-results/custom-policy-results.actions';
import { reloadGroup, reloadGroupCodeCompliance } from '../deliveries/deliveries.actions';
import { selectSelectedDelivery, selectSelectedGroupComplianceMode } from '../deliveries/deliveries.selectors';
import { loadPipelineRunRequirements, loadPipelineRunRequirementTestcases } from '../fc-2/fc-2.actions';
import { loadFosstarsSingleSummary } from '../fosstars/fosstars.actions';
import { loadPipelineRunMalwarescanInformation } from '../malwarescan/malwarescan.actions';
import { loadPipelinesForDelivery } from '../pipeline/pipeline.actions';
import { selectPipelineById } from '../pipeline/pipeline.selectors';
import { loadPipelineRunProcessingStatus } from '../processing-status/processing-status.actions';
import {
  selectExpectedCentralPolicyKeysOfPipelineReleaseView,
  selectPipelineRunIdOfPipelineReleaseView,
  selectPipelineRunKeyOfPipelineReleaseView,
} from '../routing/routing.selectors';
import { loadPipelineRunSonarQubeCoverage } from '../sonar-qube-coverage/sonar-qube-coverage.actions';
import { loadPipelineRunRegressionTests } from '../test-regression/test-regression.actions';
import {
  deletePipelineRun,
  deletePipelineRunFailed,
  deletePipelineRunSuccessful,
  loadPipelineRunsForPipeline,
  loadPipelineRunsForPipelineReadScheduled,
  loadPipelineRunsForPipelineReleaseView,
  loadPipelineRunsForPipelineReleaseViewReadScheduled,
  revertReleaseCandidateStatusFailed,
  revertReleaseStatus,
  revertReleaseStatusIfPossible,
  revertReleaseStatusSuccessful,
  revertReleaseStatusSuccessfulId,
  setNewReleaseStatus,
  setNewReleaseStatusFailed,
  setNewReleaseStatusIfPossible,
  setNewReleaseStatusSuccessful,
  setPipelineRunsForPipeline,
  setPipelineRunsForPipelineReleaseView,
  setSelectedPipelineRun,
} from './pipeline-run.actions';
import {
  selectPipelineRunDetailForPipeline,
  selectPipelineRunsSortedForPipeline,
  selectSelectedPipelineRunForPipeline,
} from './pipeline-run.selectors';

@Injectable()
export class PipelineRunEffects {
  private readonly action$ = inject(Actions);
  private readonly accessApiService = inject(AccessApiService);
  private readonly store$ = inject(Store);
  private readonly fileParserService = inject(FileParserService);
  private readonly alertService = inject(AlertService);

  public onLoadPipelineRunsForPipeline = createEffect(() => {
    return this.action$.pipe(
      ofType(loadPipelineRunsForPipeline),
      mergeMap((action) => {
        return this.accessApiService.getPipelineRunsForPipeline(action.pipelineId, action.groupId, false).pipe(
          map((pipelineRuns) => setPipelineRunsForPipeline(action.pipelineId, pipelineRuns, action.updateSelectedPipelineRun, false)),
          takeUntil(
            this.action$.pipe(
              ofType(loadPipelineRunsForPipeline),
              filter((newAction) => newAction.groupId === action.groupId && newAction.pipelineId === action.pipelineId),
            ),
          ),
        );
      }),
    );
  });

  /**
   * This action is to trigger the loading of scheduled info for pipeline runs after the initial load if not already present
   * This is for improving the performance, as loading scheduled info for all pipeline runs can take some time
   */
  public onLoadPipelineRunsForPipelineTriggerReadScheduled = createEffect(() => {
    return this.action$.pipe(
      ofType(setPipelineRunsForPipeline),
      filter((action) => !action.hasScheduledInfo),
      concatLatestFrom(() => this.store$.select(selectSelectedDelivery)),
      map(([setPipelineRunsAction, selectedDelivery]) => {
        return loadPipelineRunsForPipelineReadScheduled(setPipelineRunsAction.pipelineId, selectedDelivery.id);
      }),
    );
  });

  /**
   * This action is to trigger the loading of scheduled info for pipeline runs after the initial load if not already present
   * This is for improving the performance, as loading scheduled info for all pipeline runs can take some time
   */
  public onLoadPipelineRunsForPipelineReadScheduled = createEffect(() => {
    return this.action$.pipe(
      ofType(loadPipelineRunsForPipelineReadScheduled),
      mergeMap((action) => {
        return this.accessApiService.getPipelineRunsForPipeline(action.pipelineId, action.groupId, true).pipe(
          // do not update the selected pipeline run here, as it should already be set and to not reload cards unnecessarily
          map((pipelineRuns) => setPipelineRunsForPipeline(action.pipelineId, pipelineRuns, false, true)),
          takeUntil(
            this.action$.pipe(
              ofType(loadPipelineRunsForPipelineReadScheduled),
              filter((newAction) => newAction.groupId === action.groupId && newAction.pipelineId === action.pipelineId),
            ),
          ),
        );
      }),
    );
  });

  public onDeletePipelineRunForPipeline = createEffect(() => {
    return this.action$.pipe(
      ofType(deletePipelineRun),
      switchMap((action) => {
        return this.fileParserService.deletePipelineRun(action.pipelineRunId).pipe(
          map(() =>
            deletePipelineRunSuccessful({
              pipelineId: action.pipelineId,
              pipelineRunId: action.pipelineRunId,
              pipelineRunKey: action.pipelineRunKey,
              isLatestPipelineRun: action.isLatestPipelineRun,
            }),
          ),
          catchError(() => {
            this.alertService.createAlert(
              `The pipeline run '${action.pipelineRunKey}' could not be deleted. Please try again later.`,
              AlertType.ERROR,
            );
            return of(deletePipelineRunFailed({ pipelineRunId: action.pipelineRunId, pipelineId: action.pipelineId }));
          }),
        );
      }),
    );
  });

  public onSetPipelineRunsForPipelineToSetSelectedPipelineRun = createEffect(() => {
    return this.action$.pipe(
      ofType(setPipelineRunsForPipeline),
      mergeMap((action) => of(action).pipe(withLatestFrom(this.store$.select(selectSelectedPipelineRunForPipeline(action.pipelineId))))),
      switchMap(([action, selectedPipelineRunId]) => {
        if (action.updateSelectedPipelineRun) {
          // use selected pipeline run from store or latest pipeline run from pipeline when pipeline gets expanded
          return [setSelectedPipelineRun(action.pipelineId, selectedPipelineRunId || action.pipelineRuns[0]?.id)];
        }
        return [];
      }),
    );
  });

  public onPipelineRunDeletionSuccessfulToLoadDeliverySuccessful = createEffect(() => {
    return this.action$.pipe(
      ofType(deletePipelineRunSuccessful),
      mergeMap((action) => of(action).pipe(withLatestFrom(this.store$.select(selectPipelineRunsSortedForPipeline(action.pipelineId))))),
      switchMap(([action, pipelineRunsSorted]) => {
        if (action.isLatestPipelineRun) {
          // when the latest pipeline run was deleted, we need to reload the group to get the new latest pipeline run
          // also in the pipeline list (table)
          return [reloadGroup()];
        } else {
          // there must be at least one more pipeline run,
          // otherwise we would have delete the latest one and end up in the if branch above
          return [setSelectedPipelineRun(action.pipelineId, pipelineRunsSorted[0].id)];
        }
      }),
    );
  });

  public postPipelineRunDeletedMessageOnPipelineRunDeletionSuccessful = createEffect(() => {
    return this.action$.pipe(
      ofType(deletePipelineRunSuccessful),
      mergeMap((action) => {
        this.postMessageToParentWindow('PipelineRunDeleted', {
          pipelineRunId: action.pipelineRunId,
          pipelineRunKey: action.pipelineRunKey,
        });
        return [];
      }),
    );
  });

  public onSetSelectedPipelineRunToLoadTileData = createEffect(() => {
    return this.action$.pipe(
      ofType(setSelectedPipelineRun),
      filter((action) => !!action.selectedPipelineRunId),
      concatLatestFrom((action) => [this.store$.select(selectExpectedCentralPolicyKeysOfPipelineReleaseView)]),
      switchMap(([action, expectedCentralPolicyKeys]) => [
        loadPipelineRunATCInformation({ pipelineRunId: action.selectedPipelineRunId }),
        loadPipelineRunRequirementTestcases({ pipelineRunId: action.selectedPipelineRunId }),
        loadPipelineRunRegressionTests({
          pipelineRunId: action.selectedPipelineRunId,
          pipelineId: action.pipelineId,
        }),
        loadCodeCoverageForPipelineRun({ pipelineRunId: action.selectedPipelineRunId }),
        loadPipelineRunMalwarescanInformation({ pipelineRunId: action.selectedPipelineRunId }),
        loadApiMetadataPipelineRunSummary({ pipelineRunId: action.selectedPipelineRunId }),
        loadPipelineRunRequirements({ pipelineRunId: action.selectedPipelineRunId }),
        loadPipelineRunSonarQubeCoverage({ pipelineRunId: action.selectedPipelineRunId }),
        loadPipelineRunProcessingStatus({ pipelineRunId: action.selectedPipelineRunId }),
        loadFosstarsSingleSummary({ pipelineRunId: action.selectedPipelineRunId }),
        loadCentralPolicyResultsSingleSummary({
          pipelineRunId: action.selectedPipelineRunId,
          expectedPolicyKeys: expectedCentralPolicyKeys,
        }),
        loadCustomPolicyResultsSingleSummary({ pipelineRunId: action.selectedPipelineRunId }),
      ]),
    );
  });

  public onSetReleaseStatusSuccessfulLoadRuns = createEffect(() => {
    return this.action$.pipe(
      ofType(setNewReleaseStatusSuccessful, revertReleaseStatusSuccessful),
      mergeMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.store$.select(selectPipelineById(action.pipelineId)),
            this.store$.select(selectSelectedGroupComplianceMode),
            this.store$.select(selectSelectedDelivery),
          ),
        ),
      ),
      mergeMap(([action, pipeline, complianceMode, selectedDelivery]) => {
        const actionsToTrigger: Action[] = [
          loadPipelineRunsForPipeline(action.pipelineId, action.groupId, action.releaseStatus === ReleaseStatusKey.RELEASED),
        ];
        if (pipeline.pipelineRunId === action.pipelineRunId && selectedDelivery.id === action.groupId) {
          const delivery: Delivery = { id: action.groupId } as Delivery;
          actionsToTrigger.push(loadPipelinesForDelivery({ delivery: delivery }));
        }

        if (complianceMode.getReleaseStatus().key === action.releaseStatus || action.type === revertReleaseStatusSuccessfulId) {
          actionsToTrigger.push(reloadGroupCodeCompliance());
        }
        return actionsToTrigger;
      }),
    );
  });

  public postPipelineRunReleaseStatusChangedMessageOnSetReleaseStatusSuccessful = createEffect(() => {
    return this.action$.pipe(
      ofType(setNewReleaseStatusSuccessful, revertReleaseStatusSuccessful),
      concatLatestFrom((action) => [
        this.store$.select(selectSelectedDelivery),
        this.store$.select(selectPipelineRunDetailForPipeline(action.pipelineId, action.pipelineRunId)),
      ]),
      mergeMap(([action, selectedDelivery, pipelineRun]) => {
        // Note: We only post the message if the release status change happened in the currently viewed delivery / microdelivery
        // suppressing a second message for the MDG of a currently viewed microdelivery
        if (selectedDelivery.id === action.groupId) {
          this.postMessageToParentWindow('PipelineRunReleaseStatusChanged', {
            pipelineRunId: action.pipelineRunId,
            pipelineRunKey: pipelineRun.pipelineRunKey,
            releaseStatus: action.releaseStatus,
            reverted: action.type === revertReleaseStatusSuccessfulId,
          });
        }
        return [];
      }),
    );
  });

  public onPipelineRunReleaseStatus = createEffect(() => {
    return this.action$.pipe(
      ofType(setNewReleaseStatus),
      mergeMap((action) => {
        return this.fileParserService.setPipelineRunReleaseStatus(action.pipelineRunId, action.groupId, action.releaseStatus).pipe(
          // delay reloading to increase the chances of receiving updated values for the documents tile
          delayWhen(() => (action.releaseStatus === ReleaseStatusKey.RELEASED ? timer(3000) : of(0))),
          map(() =>
            setNewReleaseStatusSuccessful({
              pipelineId: action.pipelineId,
              pipelineRunId: action.pipelineRunId,
              groupId: action.groupId,
              releaseStatus: action.releaseStatus,
            }),
          ),
          catchError(() => {
            const errorMsg = this.buildErrorMsg(
              action.pipelineRunKey,
              action.isMicrodelivery,
              action.groupName,
              action.groupKey,
              action.releaseStatus,
              'updated',
            );

            this.alertService.createAlert(errorMsg, AlertType.ERROR);
            return of(
              setNewReleaseStatusFailed({
                pipelineId: action.pipelineId,
                pipelineRunId: action.pipelineRunId,
                groupId: action.groupId,
                releaseStatus: action.releaseStatus,
              }),
            );
          }),
          takeUntil(
            this.action$.pipe(
              ofType(setNewReleaseStatus),
              filter((newAction) => newAction.groupId === action.groupId && newAction.pipelineRunId === action.pipelineRunId),
            ),
          ),
        );
      }),
    );
  });

  public onSetPipelineRunReleaseStatusIfPossible = createEffect(() => {
    return this.action$.pipe(
      ofType(setNewReleaseStatusIfPossible),
      switchMap((action) => {
        return this.fileParserService.getPossibleReleaseStatus(action.pipelineRunId, action.groupId).pipe(
          map((result) => {
            const statusIsPossible = !!result.find((entry) => entry.key === action.releaseStatus);
            if (statusIsPossible) {
              return setNewReleaseStatus({
                pipelineId: action.pipelineId,
                pipelineRunId: action.pipelineRunId,
                pipelineRunKey: action.pipelineRunKey,
                groupId: action.groupId,
                groupKey: action.groupKey,
                groupName: action.groupName,
                isMicrodelivery: action.isMicrodelivery,
                releaseStatus: action.releaseStatus,
              });
            }
          }),
          catchError(() => {
            const errorMsg = this.buildErrorMsg(
              action.pipelineRunKey,
              action.isMicrodelivery,
              action.groupName,
              action.groupKey,
              action.releaseStatus,
              'updated',
            );

            this.alertService.createAlert(errorMsg, AlertType.ERROR);
            return of(
              setNewReleaseStatusFailed({
                pipelineId: action.pipelineId,
                pipelineRunId: action.pipelineRunId,
                groupId: action.groupId,
                releaseStatus: action.releaseStatus,
              }),
            );
          }),
        );
      }),
    );
  });

  public onRevertPipelineRunReleaseStatusIfPossible = createEffect(() => {
    return this.action$.pipe(
      ofType(revertReleaseStatusIfPossible),
      switchMap((action) => {
        return this.fileParserService.getReleaseStatuses(action.pipelineRunId, [...action.microdeliveryIds, action.groupId]).pipe(
          map((result) => {
            const hasReleaseStatus = !!result.find(
              (entry) =>
                entry.releaseStatus.key === action.releaseStatus &&
                entry.groupKey === action.groupKey &&
                entry.groupTypeKey === GroupTypeKey.DELIVERY,
            );

            const hasReleaseStatusInMicrodelivery = !!result.find(
              (entry) => entry.releaseStatus.key === action.releaseStatus && entry.groupTypeKey !== GroupTypeKey.DELIVERY,
            );

            const revertIsPossible = hasReleaseStatus && !hasReleaseStatusInMicrodelivery;
            if (revertIsPossible) {
              return revertReleaseStatus({
                pipelineId: action.pipelineId,
                pipelineRunId: action.pipelineRunId,
                pipelineRunKey: action.pipelineRunKey,
                groupId: action.groupId,
                groupKey: action.groupKey,
                groupName: action.groupName,
                isMicrodelivery: action.isMicrodelivery,
                releaseStatus: action.releaseStatus,
                justification: action.justification,
              });
            }
          }),
          catchError(() => {
            const errorMsg = this.buildErrorMsg(
              action.pipelineRunKey,
              action.isMicrodelivery,
              action.groupName,
              action.groupKey,
              action.releaseStatus,
              'reverted',
            );

            this.alertService.createAlert(errorMsg, AlertType.ERROR);

            return of(
              revertReleaseCandidateStatusFailed({
                pipelineId: action.pipelineId,
                pipelineRunId: action.pipelineRunId,
                groupId: action.groupId,
                releaseStatus: action.releaseStatus,
                justification: action.justification,
              }),
            );
          }),
        );
      }),
    );
  });

  public onRevertPipelineRunReleaseStatus = createEffect(() => {
    return this.action$.pipe(
      ofType(revertReleaseStatus),
      mergeMap((action) => {
        return this.fileParserService
          .revertPipelineRunReleaseStatus(action.pipelineRunId, action.groupId, action.releaseStatus, action.justification)
          .pipe(
            map(() =>
              revertReleaseStatusSuccessful({
                pipelineId: action.pipelineId,
                pipelineRunId: action.pipelineRunId,
                groupId: action.groupId,
                releaseStatus: action.releaseStatus,
                justification: action.justification,
              }),
            ),
            catchError(() => {
              const errorMsg = this.buildErrorMsg(
                action.pipelineRunKey,
                action.isMicrodelivery,
                action.groupName,
                action.groupKey,
                action.releaseStatus,
                'reverted',
              );

              this.alertService.createAlert(errorMsg, AlertType.ERROR);
              return of(
                revertReleaseCandidateStatusFailed({
                  pipelineId: action.pipelineId,
                  pipelineRunId: action.pipelineRunId,
                  groupId: action.groupId,
                  releaseStatus: action.releaseStatus,
                  justification: action.justification,
                }),
              );
            }),
            takeUntil(
              this.action$.pipe(
                ofType(revertReleaseStatus),
                filter((newAction) => newAction.groupId === action.groupId && newAction.pipelineRunId === action.pipelineRunId),
              ),
            ),
          );
      }),
    );
  });

  public onLoadPipelineRunsForPipelineReleaseView = createEffect(() => {
    return this.action$.pipe(
      ofType(loadPipelineRunsForPipelineReleaseView),
      mergeMap((action) => {
        return this.accessApiService.getPipelineRunsForPipeline(action.pipelineId, action.groupId, false).pipe(
          map((pipelineRuns) => setPipelineRunsForPipelineReleaseView(action.pipelineId, pipelineRuns, true, false)),
          // TODO we should be able to remove the takeUntil, as we have no navigation between pipelines at this point.
          takeUntil(
            this.action$.pipe(
              ofType(loadPipelineRunsForPipelineReleaseView),
              filter((newAction) => newAction.groupId === action.groupId && newAction.pipelineId === action.pipelineId),
            ),
          ),
        );
      }),
    );
  });

  /**
   * This action is to trigger the loading of scheduled info for pipeline runs after the initial load if not already present
   * This is for improving the performance, as loading scheduled info for all pipeline runs can take some time
   */
  public onLoadPipelineRunsForPipelineReleaseViewTriggerReadScheduled = createEffect(() => {
    return this.action$.pipe(
      ofType(setPipelineRunsForPipelineReleaseView),
      filter((action) => !action.hasScheduledInfo),
      concatLatestFrom(() => this.store$.select(selectSelectedDelivery)),
      map(([setPipelineRunsAction, selectedDelivery]) => {
        return loadPipelineRunsForPipelineReleaseViewReadScheduled(setPipelineRunsAction.pipelineId, selectedDelivery.id);
      }),
    );
  });

  /**
   * This action is to trigger the loading of scheduled info for pipeline runs after the initial load if not already present
   * This is for improving the performance, as loading scheduled info for all pipeline runs can take some time
   */
  public onLoadPipelineRunsForPipelineReleaseViewReadScheduled = createEffect(() => {
    return this.action$.pipe(
      ofType(loadPipelineRunsForPipelineReleaseViewReadScheduled),
      mergeMap((action) => {
        return this.accessApiService.getPipelineRunsForPipeline(action.pipelineId, action.groupId, true).pipe(
          // do not update the selected pipeline run here, as it should already be set and to not reload cards unnecessarily
          map((pipelineRuns) => setPipelineRunsForPipelineReleaseView(action.pipelineId, pipelineRuns, false, true)),
          // TODO we should be able to remove the takeUntil, as we have no navigation between pipelines at this point.
          takeUntil(
            this.action$.pipe(
              ofType(loadPipelineRunsForPipelineReleaseViewReadScheduled),
              filter((newAction) => newAction.groupId === action.groupId && newAction.pipelineId === action.pipelineId),
            ),
          ),
        );
      }),
    );
  });

  public onSetPipelineRunsForPipelineReleaseViewToSetSelectedPipelineRun = createEffect(() => {
    return this.action$.pipe(
      ofType(setPipelineRunsForPipelineReleaseView),
      concatLatestFrom(() => [
        this.store$.select(selectPipelineRunIdOfPipelineReleaseView),
        this.store$.select(selectPipelineRunKeyOfPipelineReleaseView),
      ]),
      switchMap(([action, selectedPipelineRunId, selectedPipelineRunKey]) => {
        if (!action.updateSelectedPipelineRun) {
          return EMPTY;
        }
        if (selectedPipelineRunId) {
          // if id is given it has priority over pipelineRunKey
          const selectedPipelineRun = action.pipelineRuns.find((run) => run.id === selectedPipelineRunId);
          if (selectedPipelineRunKey) {
            return of(setSelectedPipelineRun(action.pipelineId, selectedPipelineRun.id));
          } else {
            this.alertService.createAlert(`No pipeline run with id '${selectedPipelineRunId}' found.`, AlertType.WARNING);
          }
        }
        if (selectedPipelineRunKey) {
          const selectedPipelineRun = action.pipelineRuns.find((run) => run.pipelineRunKey === selectedPipelineRunKey);
          if (selectedPipelineRun) {
            return of(setSelectedPipelineRun(action.pipelineId, selectedPipelineRun.id));
          } else {
            this.alertService.createAlert(`No pipeline run with key '${selectedPipelineRunKey}' found.`, AlertType.WARNING);
          }
        }
        // use selected pipeline run from store or latest pipeline run from pipeline when pipeline gets expanded
        return of(setSelectedPipelineRun(action.pipelineId, action.pipelineRuns[0]?.id));
      }),
    );
  });

  private buildErrorMsg(
    pipelineRunKey: string,
    isMicrodelivery: boolean,
    groupName: string,
    groupKey: string,
    releaseStatus: string,
    operation: string,
  ) {
    let errorMsg = `The pipeline run '${pipelineRunKey}' for `;
    errorMsg += isMicrodelivery ? `microdelivery '${groupName}' ` : `delivery '${groupKey}' `;
    errorMsg += `could not be ${operation} by release status '${releaseStatus}'. Please try again later.`;
    return errorMsg;
  }

  private postMessageToParentWindow(type: string, data: any) {
    const message = {
      type: type,
      data: data,
    };
    console.log(`Posting message to parent window: ${JSON.stringify(message)}`);
    const targetWindow = window.parent;
    if (targetWindow && targetWindow !== window) {
      targetWindow.postMessage(message, { targetOrigin: '*' });
    }
  }
}
